/**
 * Audit Log Page Component
 * Fetches and displays server logs from the admin logs API
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './AuditLogPage.css';

const LINES_OPTIONS = [100, 500, 1000];

const AuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [totalLines, setTotalLines] = useState(0);
  const [lines, setLines] = useState(100);
  const [filter, setFilter] = useState('');
  const [filterApplied, setFilterApplied] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getAdminLogs(lines, filterApplied || undefined);
      setLogs(result.logs || []);
      setTotalLines(result.totalLines ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
      setLogs([]);
      setTotalLines(0);
    } finally {
      setLoading(false);
    }
  }, [lines, filterApplied]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleApplyFilter = () => {
    setFilterApplied(filter.trim());
  };

  const handleClearFilter = () => {
    setFilter('');
    setFilterApplied('');
  };

  return (
    <div className="audit-log-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-sub">Server logs from api.log</p>
        </div>
      </div>

      <div className="audit-controls">
        <div className="control-group">
          <label>Lines:</label>
          <select value={lines} onChange={(e) => setLines(Number(e.target.value))}>
            {LINES_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group filter-group">
          <label>Filter:</label>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilter()}
            placeholder="Filter log lines..."
          />
          <button className="btn btn-secondary btn-sm" onClick={handleApplyFilter}>
            Apply
          </button>
          {filterApplied && (
            <button className="btn btn-secondary btn-sm" onClick={handleClearFilter}>
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <span className="card-title">Log entries</span>
          <span className="log-meta">
            Showing {logs.length} of {totalLines} lines
            {filterApplied && ` (filtered by "${filterApplied}")`}
          </span>
        </div>
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              Loading...
            </div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
              No log entries found
            </div>
          ) : (
            <pre className="log-content">
              <code>{logs.join('\n')}</code>
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
